import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import MedicalHistoryMultiSelect from '../ui/medical-history-multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, ChevronDown, User, Phone, MapPin, Truck, FileText, HeartPulse, ShieldCheck } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import * as z from 'zod';
import { patientFormPublicSchema } from '@/validators/patientFormPublic.schema';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { State } from '@/models/state.model';
import { stateService } from '@/services/stateService';
import { useToast } from '@/hooks/use-toast';
import { DatePicker } from '@/components/ui/date-picker';
import { format } from 'date-fns';
import { Form } from '../ui/form';
import PhoneInput from '../ui/phone-input';
import AddressInput, { AddressData } from '../ui/AddressInput';
import BloodPressureInput from '../ui/blood-pressure-input';
import HeightInput from '../ui/height-input';
import WeightInput from '../ui/weight-input';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import AllergyMultiSelect from '../ui/allergy-multi-select';

type PatientFormData = z.infer<typeof patientFormPublicSchema>;

interface PatientSelfServiceFormProps {
  clinicName: string;
  patientData?: any;
  onSubmit: (data: PatientFormData) => Promise<void>;
  isLoading?: boolean;
}

const createContactId = () => {
  if (typeof globalThis.crypto !== 'undefined' && 'randomUUID' in globalThis.crypto) {
    return globalThis.crypto.randomUUID();
  }
  return `contact_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const createContact = (key: string) => ({
  id: createContactId(),
  key,
  value: '',
  is_default: false,
});

const normalizeContacts = (contacts?: PatientFormData['contacts']) => {
  const baseContacts =
    contacts && contacts.length > 0 ? contacts : [createContact('email'), createContact('phone')];
  return baseContacts.map(contact => ({
    ...contact,
    id: contact.id ?? createContactId(),
    key: contact.key || 'email',
    value: contact.value ?? '',
    is_default: contact.is_default ?? false,
  }));
};

const PatientSelfServiceForm = ({
  clinicName,
  patientData,
  onSubmit,
  isLoading = false,
}: PatientSelfServiceFormProps) => {
  const [notesRows, setNotesRows] = useState(8);
  const [states, setStates] = useState<State[]>([]);
  const { toast } = useToast();

  const [isBasicOpen, setIsBasicOpen] = useState(true);
  const [isContactOpen, setIsContactOpen] = useState(true);
  const [isBillingOpen, setIsBillingOpen] = useState(true);
  const [isShippingOpen, setIsShippingOpen] = useState(true);
  const [isClinicalOpen, setIsClinicalOpen] = useState(false);
  const [isMedicalOpen, setIsMedicalOpen] = useState(true);
  const [isInsuranceOpen, setIsInsuranceOpen] = useState(true);

  const form = useForm<PatientFormData>({
    resolver: zodResolver(patientFormPublicSchema),
    defaultValues: {
      first_name: '',
      middle_name: '',
      last_name: '',
      gender: '',
      dob: '',
      species: 'human',
      animal_owner_name: '',
      weight: '',
      weight_recorded_date: '',
      height: undefined,
      blood_pressure_systolic: '',
      blood_pressure_diastolic: '',
      blood_pressure_recorded_at: '',
      medication_allergies: [],
      no_known_allergies: false,
      medical_history: '',
      preferred_contact_methods: {
        email: false,
        sms: false,
        phone: false,
      },
      emergency_contact_phone: '',
      insurance_carrier: '',
      insurance_member_id: '',
      insurance_group_id: '',
      contacts: [createContact('email'), createContact('phone')],
      shipping_same_as_billing: true,
      addresses: [
        {
          address_type: 'billing' as const,
          addressLine1: '',
          addressLine2: '',
          state: '',
          city: '',
          postalCode: '',
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'contacts',
    keyName: 'fieldId',
  });

  const shippingSameAsBilling = form.watch('shipping_same_as_billing');

  // Ensure at least one default email and one default phone
  useEffect(() => {
    const ensureDefault = (type: 'email' | 'phone') => {
      const currentContacts = form.getValues('contacts') || [];
      const hasDefault = currentContacts.some(
        contact => contact.key === type && contact.is_default
      );
      if (hasDefault) return;
      const firstIndex = currentContacts.findIndex(contact => contact.key === type);
      if (firstIndex === -1) return;
      const updatedContacts = currentContacts.map((contact, index) => {
        if (contact.key !== type) return contact;
        return { ...contact, is_default: index === firstIndex };
      });
      form.setValue('contacts', updatedContacts, {
        shouldDirty: false,
        shouldValidate: false,
      });
    };
    ensureDefault('email');
    ensureDefault('phone');
  }, [fields, form]);

  const today = format(new Date(), 'yyyy-MM-dd');

  // Handle address change from AddressInput component
  const handleAddressChange = (index: number) => (addressData: AddressData) => {
    form.setValue(`addresses.${index}.addressLine1`, addressData.addressLine1, {
      shouldValidate: true,
    });
    form.setValue(`addresses.${index}.addressLine2`, addressData.addressLine2 || '', {
      shouldValidate: false,
    });
    form.setValue(`addresses.${index}.city`, addressData.city, { shouldValidate: true });
    form.setValue(`addresses.${index}.state`, addressData.state, { shouldValidate: true });
    form.setValue(`addresses.${index}.postalCode`, addressData.postalCode || '', {
      shouldValidate: true,
    });
  };

  const handleSubmit = async (data: PatientFormData) => {
    // Sanitize height
    if (data.height !== undefined && data.height !== null) {
      const heightValue = parseFloat(String(data.height));
      data.height = isNaN(heightValue) ? undefined : heightValue;
    }

    // Sanitize weight
    if (data.weight !== undefined && data.weight !== null) {
      const weightStr = String(data.weight).trim();
      data.weight = weightStr === '' || weightStr === 'NaN' ? '' : weightStr;
    }

    // If shipping same as billing, only send billing address
    if (data.shipping_same_as_billing) {
      data.addresses = data.addresses.filter(a => a.address_type === 'billing');
    }

    await onSubmit(data);
  };

  useEffect(() => {
    if (states.length === 0) {
      stateService.getAllStates().then(result => {
        setStates(result.data?.states);
      });
    }
  }, [states]);

  // Watch for changes in medication_allergies
  const medicationAllergies = form.watch('medication_allergies');
  const noKnownAllergies = form.watch('no_known_allergies');

  useEffect(() => {
    const allergies = medicationAllergies || [];
    if (allergies.length > 0 && noKnownAllergies) {
      form.setValue('no_known_allergies', false, { shouldValidate: true });
    }
  }, [medicationAllergies, noKnownAllergies, form]);

  useEffect(() => {
    if (patientData) {
      form.reset({
        first_name: patientData.first_name || '',
        middle_name: patientData.middle_name || '',
        last_name: patientData.last_name || '',
        gender: patientData.gender || '',
        dob: patientData.dob || '',
        species: patientData.species || 'human',
        contacts: normalizeContacts(patientData.contacts),
        shipping_same_as_billing: (() => {
          const addrs = patientData.addresses || [];
          const hasShipping = addrs.some((a: any) => a.address_type === 'shipping');
          return !hasShipping;
        })(),
        addresses: (() => {
          const addrs = patientData.addresses || [];
          if (addrs.length === 0) {
            return [{
              address_type: 'billing' as const,
              addressLine1: '',
              addressLine2: '',
              state: '',
              city: '',
              postalCode: '',
            }];
          }
          // Ensure each address has an address_type, default to 'billing'
          return addrs.map((a: any, i: number) => ({
            address_type: a.address_type || (i === 0 ? 'billing' : 'shipping'),
            addressLine1: a.addressLine1 || '',
            addressLine2: a.addressLine2 || '',
            city: a.city || '',
            state: a.state || '',
            postalCode: a.postalCode || '',
          }));
        })(),
        weight: patientData.weight?.toString() || '',
        weight_recorded_date: patientData.weight_recorded_at
          ? format(new Date(patientData.weight_recorded_at), 'MM/dd/yyyy')
          : undefined,
        height: (() => {
          const h = parseFloat(patientData.height?.toString() || '');
          return isNaN(h) || h === 0 ? undefined : h;
        })(),
        blood_pressure_systolic: patientData.blood_pressure_systolic ?? '',
        blood_pressure_diastolic: patientData.blood_pressure_diastolic ?? '',
        blood_pressure_recorded_at: patientData.blood_pressure_recorded_at || '',
        medication_allergies: patientData.medication_allergies || [],
        no_known_allergies:
          !patientData.medication_allergies || patientData.medication_allergies.length === 0,
        medical_history: patientData.medical_history || '',
        preferred_contact_methods: patientData.preferred_contact_methods || {
          email: false,
          sms: false,
          phone: false,
        },
        emergency_contact_phone: patientData.emergency_contact_phone || '',
        insurance_carrier: patientData.insurance_carrier || '',
        insurance_member_id: patientData.insurance_member_id || '',
        insurance_group_id: patientData.insurance_group_id || '',
      });
    }
  }, [patientData]);

  return (
    <div className='mx-auto w-full max-w-4xl p-6'>
      {/* Header */}
      <div className='mb-8'>
        <h1 className='mb-2 text-3xl font-bold text-slate-900'>Patient Information Form</h1>
        <p className='text-slate-600'>
          Complete your information for {clinicName}. All fields marked with{' '}
          <span className='text-red-500'>*</span> are required.
        </p>
      </div>

      <Form {...form}>
        <form id='patient-form' onSubmit={form.handleSubmit(handleSubmit)} className='space-y-8'>
          {/* Basic Patient Info */}
          <Collapsible open={isBasicOpen} onOpenChange={setIsBasicOpen} className='rounded-lg border border-slate-200 bg-white px-4'>
            <CollapsibleTrigger asChild>
              <button type='button' className='flex w-full items-center justify-between py-3 text-sm font-semibold text-[#60378B] hover:text-[#4c2c6f]'>
                <span className='flex items-center gap-2'>
                  <User className='h-4 w-4' />
                  BASIC INFORMATION
                  {(form.formState.errors.first_name || form.formState.errors.last_name || form.formState.errors.gender || form.formState.errors.dob) && (
                    <span className='h-2 w-2 rounded-full bg-red-500' />
                  )}
                </span>
                <div className='flex items-center gap-2'>
                  <span className='text-xs font-normal text-slate-400'>Required</span>
                  <ChevronDown className={cn('h-4 w-4 transition-transform', isBasicOpen && 'rotate-180')} />
                </div>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className='space-y-4 pb-4 pt-2'>
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              <div className='space-y-1.5'>
                <Label htmlFor='first_name'>
                  First Name <span className='text-red-500'>*</span>
                </Label>
                <Input
                  id='first_name'
                  placeholder='Enter first name'
                  {...form.register('first_name')}
                  onChange={e => {
                    const value = e.target.value || '';
                    form.setValue('first_name', value.trim(), { shouldValidate: true });
                  }}
                />
                {form.formState.errors.first_name && (
                  <p className='text-[0.8rem] text-red-500'>
                    {form.formState.errors.first_name.message}
                  </p>
                )}
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='middle_name'>Middle Name</Label>
                <Input
                  id='middle_name'
                  placeholder='Enter middle name'
                  {...form.register('middle_name')}
                  onChange={e => {
                    const value = e.target.value || '';
                    form.setValue('middle_name', value.trim(), { shouldValidate: true });
                  }}
                />
              </div>
            </div>
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              <div className='space-y-1.5'>
                <Label htmlFor='last_name'>
                  Last Name <span className='text-red-500'>*</span>
                </Label>
                <Input
                  id='last_name'
                  placeholder='Enter last name'
                  {...form.register('last_name')}
                  onChange={e => {
                    const value = e.target.value || '';
                    form.setValue('last_name', value.trim(), { shouldValidate: true });
                  }}
                />
                {form.formState.errors.last_name && (
                  <p className='text-[0.8rem] text-red-500'>
                    {form.formState.errors.last_name.message}
                  </p>
                )}
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='gender'>
                  Gender <span className='text-red-500'>*</span>
                </Label>
                <Select
                  value={form.watch('gender')}
                  onValueChange={value =>
                    form.setValue('gender', value || '', { shouldValidate: true })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select gender' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='male'>Male</SelectItem>
                    <SelectItem value='female'>Female</SelectItem>
                    {/* <SelectItem value="other">Other</SelectItem> */}
                    <SelectItem value='unknown'>Unknown</SelectItem>
                  </SelectContent>
                </Select>
                {form.formState.errors.gender && (
                  <p className='text-[0.8rem] text-red-500'>
                    {form.formState.errors.gender.message}
                  </p>
                )}
              </div>
            </div>

            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              <div className='space-y-1.5'>
                <Label htmlFor='dob'>
                  Date of Birth <span className='text-red-500'>*</span>
                </Label>
                <DatePicker
                  value={
                    form.watch('dob')
                      ? (() => {
                          const dateStr = form.watch('dob').split('T')[0];
                          const [year, month, day] = dateStr.split('-');
                          return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                        })()
                      : undefined
                  }
                  onValueChange={date => {
                    const dateString = date ? format(new Date(date), 'yyyy-MM-dd') : '';
                    form.setValue('dob', dateString + 'T00:00:00.000Z', {
                      shouldValidate: true,
                    });
                  }}
                  placeholder='Select date of birth'
                  maxDate={new Date()}
                  minDate={new Date('1900-01-01')}
                />
                {form.formState.errors.dob && (
                  <p className='text-[0.8rem] text-red-500'>{form.formState.errors.dob.message}</p>
                )}
              </div>
            </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Contact Information */}
          <Collapsible open={isContactOpen} onOpenChange={setIsContactOpen} className='rounded-lg border border-slate-200 bg-white px-4'>
            <CollapsibleTrigger asChild>
              <button type='button' className='flex w-full items-center justify-between py-3 text-sm font-semibold text-[#60378B] hover:text-[#4c2c6f]'>
                <span className='flex items-center gap-2'>
                  <Phone className='h-4 w-4' />
                  CONTACT INFORMATION
                  {form.formState.errors.contacts && (
                    <span className='h-2 w-2 rounded-full bg-red-500' />
                  )}
                </span>
                <div className='flex items-center gap-2'>
                  <span className='text-xs font-normal text-slate-400'>Required</span>
                  <ChevronDown className={cn('h-4 w-4 transition-transform', isContactOpen && 'rotate-180')} />
                </div>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className='space-y-4 pb-4 pt-2'>

            {/* Email Contact */}
            <div className='flex items-center justify-between gap-4'>
              <Label>Email Contact <span className='text-red-500'>*</span></Label>
              <Button
                type='button'
                variant='outline'
                size='sm'
                className='h-7 rounded-full bg-brand-teal px-3 py-1 text-sm font-normal text-white hover:bg-brand-teal/80 hover:text-white'
                onClick={() => append(createContact('email'))}
              >
                <Plus className='mr-2 h-4 w-4' /> <span className='font-normal'>Add Email</span>
              </Button>
            </div>
            {fields
              .filter(field => field.key === 'email')
              .map((field, filteredIndex) => {
                const contactIndex = fields.findIndex(item => item.id === field.id);
                if (contactIndex === -1) return null;

                const setDefaultEmail = () => {
                  const currentContacts = form.getValues('contacts') || [];
                  const updatedContacts = currentContacts.map((contact, index) => {
                    if (contact.key !== 'email') return contact;
                    return { ...contact, is_default: index === contactIndex };
                  });
                  form.setValue('contacts', updatedContacts, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                };

                return (
                  <div key={field.fieldId} className='flex items-center gap-4'>
                    <div className='flex w-full flex-col'>
                      <Input
                        {...form.register(`contacts.${contactIndex}.value`)}
                        onChange={e => {
                          const value = e.target.value || '';
                          form.setValue(`contacts.${contactIndex}.value`, value.trim(), {
                            shouldValidate: true,
                          });
                        }}
                        placeholder='Enter email'
                      />
                      {form.formState.errors.contacts?.[contactIndex]?.value && (
                        <p className='text-[0.8rem] text-red-500'>
                          {form.formState.errors.contacts[contactIndex].value?.message}
                        </p>
                      )}
                    </div>
                    <div className='flex items-center space-x-2'>
                      <input
                        type='radio'
                        name='default-email'
                        className='h-4 w-4 accent-brand-teal'
                        checked={Boolean(form.watch(`contacts.${contactIndex}.is_default`))}
                        onChange={setDefaultEmail}
                      />
                      <Label className='cursor-pointer text-xs font-normal'>Default</Label>
                    </div>
                    <div className='w-10'>
                      <TooltipProvider delayDuration={100}>
                        {filteredIndex !== 0 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type='button'
                                variant='ghost'
                                size='icon'
                                className='h-9 w-9 rounded-full border border-red-200 bg-transparent text-red-500 hover:border-red-500 hover:bg-red-500 hover:text-white'
                                onClick={() => remove(contactIndex)}
                              >
                                <Trash2 className='h-4 w-4' />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent
                              side='left'
                              className='border-slate-900 bg-slate-900 text-white'
                            >
                              <p>Remove contact</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </TooltipProvider>
                    </div>
                  </div>
                );
              })}

            {/* Phone Contact */}
            <div className='flex items-center justify-between gap-4'>
              <Label>Phone Contact <span className='text-red-500'>*</span></Label>
              <Button
                type='button'
                variant='outline'
                size='sm'
                className='h-7 rounded-full bg-brand-teal px-3 py-1 text-sm font-normal text-white hover:bg-brand-teal/80 hover:text-white'
                onClick={() => append(createContact('phone'))}
              >
                <Plus className='mr-2 h-4 w-4' /> <span className='font-normal'>Add Phone</span>
              </Button>
            </div>
            {fields
              .filter(field => field.key === 'phone')
              .map((field, filteredIndex) => {
                const contactIndex = fields.findIndex(item => item.id === field.id);
                if (contactIndex === -1) return null;

                const setDefaultPhone = () => {
                  const currentContacts = form.getValues('contacts') || [];
                  const updatedContacts = currentContacts.map((contact, index) => {
                    if (contact.key !== 'phone') return contact;
                    return { ...contact, is_default: index === contactIndex };
                  });
                  form.setValue('contacts', updatedContacts, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                };

                return (
                  <div key={field.fieldId} className='flex items-center gap-4'>
                    <div className='w-full'>
                      <PhoneInput
                        form={form}
                        name={`contacts.${contactIndex}.value`}
                        placeholder='Enter contact'
                        label=''
                        required
                      />
                    </div>
                    <div className='flex items-center space-x-2'>
                      <input
                        type='radio'
                        name='default-phone'
                        className='h-4 w-4 accent-brand-teal'
                        checked={Boolean(form.watch(`contacts.${contactIndex}.is_default`))}
                        onChange={setDefaultPhone}
                      />
                      <Label className='cursor-pointer text-xs font-normal'>Default</Label>
                    </div>
                    <div className='w-10'>
                      <TooltipProvider delayDuration={100}>
                        {filteredIndex !== 0 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type='button'
                                variant='ghost'
                                size='icon'
                                className='h-9 w-9 rounded-full border border-red-200 bg-transparent text-red-500 hover:border-red-500 hover:bg-red-500 hover:text-white'
                                onClick={() => remove(contactIndex)}
                              >
                                <Trash2 className='h-4 w-4' />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent
                              side='left'
                              className='border-slate-900 bg-slate-900 text-white'
                            >
                              <p>Remove contact</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </TooltipProvider>
                    </div>
                  </div>
                );
              })}

            {/* Preferred Contact Methods Section */}
            <div className='mt-6 space-y-4'>
              <div>
                <Label className='text-base font-medium'>Preferred Contact Methods</Label>
                <p className='m-0 text-xs leading-none text-slate-500'>
                  Select your preferred way(s) to be contacted
                </p>
              </div>
              <div className='space-y-3'>
                <div className='flex items-center space-x-2'>
                  <Checkbox
                    id='contact-email'
                    checked={form.watch('preferred_contact_methods.email') || false}
                    onCheckedChange={checked => {
                      form.setValue('preferred_contact_methods.email', checked as boolean);
                    }}
                  />
                  <Label htmlFor='contact-email' className='cursor-pointer font-normal'>
                    Email
                  </Label>
                </div>
                <div className='flex items-center space-x-2'>
                  <Checkbox
                    id='contact-sms'
                    checked={form.watch('preferred_contact_methods.sms') || false}
                    onCheckedChange={checked => {
                      form.setValue('preferred_contact_methods.sms', checked as boolean);
                    }}
                  />
                  <Label htmlFor='contact-sms' className='cursor-pointer font-normal'>
                    SMS
                  </Label>
                </div>
                <div className='flex items-center space-x-2'>
                  <Checkbox
                    id='contact-phone'
                    checked={form.watch('preferred_contact_methods.phone') || false}
                    onCheckedChange={checked => {
                      form.setValue('preferred_contact_methods.phone', checked as boolean);
                    }}
                  />
                  <Label htmlFor='contact-phone' className='cursor-pointer font-normal'>
                    Phone Call
                  </Label>
                </div>
              </div>
            </div>

            {/* Emergency Contact Section */}
            <div className='mt-6 space-y-4'>
              <div>
                <Label className='text-base font-medium'>Emergency Contact</Label>
                <p className='m-0 text-xs leading-none text-slate-500'>
                  Phone number for emergency situations
                </p>
              </div>
              <PhoneInput
                form={form}
                name='emergency_contact_phone'
                placeholder='Enter emergency contact phone'
                label=''
                required={false}
              />
            </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Billing Address */}
          <Collapsible open={isBillingOpen} onOpenChange={setIsBillingOpen} className='rounded-lg border border-slate-200 bg-white px-4'>
            <CollapsibleTrigger asChild>
              <button type='button' className='flex w-full items-center justify-between py-3 text-sm font-semibold text-[#60378B] hover:text-[#4c2c6f]'>
                <span className='flex items-center gap-2'>
                  <MapPin className='h-4 w-4' />
                  BILLING ADDRESS
                  {form.formState.errors.addresses?.[0] && (
                    <span className='h-2 w-2 rounded-full bg-red-500' />
                  )}
                </span>
                <div className='flex items-center gap-2'>
                  <span className='text-xs font-normal text-slate-400'>Required</span>
                  <ChevronDown className={cn('h-4 w-4 transition-transform', isBillingOpen && 'rotate-180')} />
                </div>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className='space-y-4 pb-4 pt-2'>
            <AddressInput
              value={{
                addressLine1: form.watch('addresses.0.addressLine1') || '',
                addressLine2: form.watch('addresses.0.addressLine2') || '',
                city: form.watch('addresses.0.city') || '',
                state: form.watch('addresses.0.state') || '',
                postalCode: form.watch('addresses.0.postalCode') || '',
              }}
              onChange={handleAddressChange(0)}
              errors={{
                addressLine1: form.formState.errors.addresses?.[0]?.addressLine1,
                addressLine2: form.formState.errors.addresses?.[0]?.addressLine2,
                city: form.formState.errors.addresses?.[0]?.city,
                state: form.formState.errors.addresses?.[0]?.state,
                postalCode: form.formState.errors.addresses?.[0]?.postalCode,
              }}
              states={states}
            />
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Shipping Address */}
          <Collapsible open={isShippingOpen} onOpenChange={setIsShippingOpen} className='rounded-lg border border-slate-200 bg-white px-4'>
            <CollapsibleTrigger asChild>
              <button type='button' className='flex w-full items-center justify-between py-3 text-sm font-semibold text-[#60378B] hover:text-[#4c2c6f]'>
                <span className='flex items-center gap-2'>
                  <Truck className='h-4 w-4' />
                  SHIPPING ADDRESS
                  {form.formState.errors.addresses?.[1] && (
                    <span className='h-2 w-2 rounded-full bg-red-500' />
                  )}
                </span>
                <div className='flex items-center gap-2'>
                  <span className='text-xs font-normal text-slate-400'>Optional</span>
                  <ChevronDown className={cn('h-4 w-4 transition-transform', isShippingOpen && 'rotate-180')} />
                </div>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className='space-y-4 pb-4 pt-2'>
            <div className='flex items-center space-x-2'>
              <Checkbox
                id='shipping-same-as-billing'
                checked={shippingSameAsBilling}
                onCheckedChange={checked => {
                  const isChecked = checked as boolean;
                  form.setValue('shipping_same_as_billing', isChecked, { shouldValidate: true });
                  if (isChecked) {
                    // Remove shipping address if it exists
                    const currentAddresses = form.getValues('addresses');
                    const billingOnly = currentAddresses.filter(a => a.address_type === 'billing');
                    form.setValue('addresses', billingOnly, { shouldValidate: true });
                  } else {
                    // Add empty shipping address
                    const currentAddresses = form.getValues('addresses');
                    const hasShipping = currentAddresses.some(a => a.address_type === 'shipping');
                    if (!hasShipping) {
                      form.setValue('addresses', [
                        ...currentAddresses,
                        {
                          address_type: 'shipping' as const,
                          addressLine1: '',
                          addressLine2: '',
                          city: '',
                          state: '',
                          postalCode: '',
                        },
                      ], { shouldValidate: false });
                    }
                  }
                }}
              />
              <Label htmlFor='shipping-same-as-billing' className='cursor-pointer font-normal'>
                Shipping address same as billing
              </Label>
            </div>

            {!shippingSameAsBilling && (
              <AddressInput
                value={{
                  addressLine1: form.watch('addresses.1.addressLine1') || '',
                  addressLine2: form.watch('addresses.1.addressLine2') || '',
                  city: form.watch('addresses.1.city') || '',
                  state: form.watch('addresses.1.state') || '',
                  postalCode: form.watch('addresses.1.postalCode') || '',
                }}
                onChange={handleAddressChange(1)}
                errors={{
                  addressLine1: form.formState.errors.addresses?.[1]?.addressLine1,
                  addressLine2: form.formState.errors.addresses?.[1]?.addressLine2,
                  city: form.formState.errors.addresses?.[1]?.city,
                  state: form.formState.errors.addresses?.[1]?.state,
                  postalCode: form.formState.errors.addresses?.[1]?.postalCode,
                }}
                states={states}
              />
            )}
            {form.formState.errors.addresses?.message && (
              <p className='text-[0.8rem] text-red-500'>
                {form.formState.errors.addresses.message}
              </p>
            )}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Insurance Information */}
          <Collapsible open={isInsuranceOpen} onOpenChange={setIsInsuranceOpen} className='rounded-lg border border-slate-200 bg-white px-4'>
            <CollapsibleTrigger asChild>
              <button type='button' className='flex w-full items-center justify-between py-3 text-sm font-semibold text-[#60378B] hover:text-[#4c2c6f]'>
                <span className='flex items-center gap-2'>
                  <ShieldCheck className='h-4 w-4' />
                  INSURANCE INFORMATION
                </span>
                <div className='flex items-center gap-2'>
                  <span className='text-xs font-normal text-slate-400'>Optional</span>
                  <ChevronDown className={cn('h-4 w-4 transition-transform', isInsuranceOpen && 'rotate-180')} />
                </div>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className='space-y-4 pb-4 pt-2'>
                <div className='space-y-1.5'>
                  <Label htmlFor='insurance_carrier'>Insurance Carrier</Label>
                  <Input
                    id='insurance_carrier'
                    placeholder='e.g. BlueCross BlueShield'
                    {...form.register('insurance_carrier')}
                  />
                  {form.formState.errors.insurance_carrier && (
                    <p className='text-[0.8rem] text-red-500'>{form.formState.errors.insurance_carrier.message}</p>
                  )}
                </div>
                <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                  <div className='space-y-1.5'>
                    <Label htmlFor='insurance_member_id'>Member ID</Label>
                    <Input
                      id='insurance_member_id'
                      placeholder='Enter member ID'
                      {...form.register('insurance_member_id')}
                    />
                    {form.formState.errors.insurance_member_id && (
                      <p className='text-[0.8rem] text-red-500'>{form.formState.errors.insurance_member_id.message}</p>
                    )}
                  </div>
                  <div className='space-y-1.5'>
                    <Label htmlFor='insurance_group_id'>Group ID</Label>
                    <Input
                      id='insurance_group_id'
                      placeholder='Enter group ID'
                      {...form.register('insurance_group_id')}
                    />
                    {form.formState.errors.insurance_group_id && (
                      <p className='text-[0.8rem] text-red-500'>{form.formState.errors.insurance_group_id.message}</p>
                    )}
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Clinical Info */}
          <Collapsible open={isClinicalOpen} onOpenChange={setIsClinicalOpen} className='rounded-lg border border-slate-200 bg-white px-4'>
            <CollapsibleTrigger asChild>
              <button type='button' className='flex w-full items-center justify-between py-3 text-sm font-semibold text-[#60378B] hover:text-[#4c2c6f]'>
                <span className='flex items-center gap-2'>
                  <FileText className='h-4 w-4' />
                  CLINICAL INFORMATION
                </span>
                <div className='flex items-center gap-2'>
                  <span className='text-xs font-normal text-slate-400'>Optional</span>
                  <ChevronDown className={cn('h-4 w-4 transition-transform', isClinicalOpen && 'rotate-180')} />
                </div>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className='space-y-4 pb-4 pt-2'>
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              <div className='space-y-1.5'>
                <WeightInput
                  form={form}
                  name='weight'
                  label='Weight'
                  placeholder='Enter weight'
                  required={false}
                />
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='weightRecordedDate'>Weight Recorded Date</Label>
                <DatePicker
                  value={
                    form.watch('weight_recorded_date')
                      ? new Date(form.watch('weight_recorded_date'))
                      : undefined
                  }
                  onValueChange={date => {
                    const dateString = date ? format(date, 'yyyy-MM-dd') : '';
                    form.setValue('weight_recorded_date', dateString, {
                      shouldValidate: true,
                    });
                  }}
                  placeholder='Select weight recorded date'
                  maxDate={new Date()}
                />
              </div>
            </div>
            <div className='space-y-1.5'>
              <HeightInput
                form={form}
                name='height'
                label='Height'
                placeholder='Enter height'
                required={false}
              />
            </div>
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              <div className='space-y-1.5'>
                <BloodPressureInput
                  form={form}
                  systolicName='blood_pressure_systolic'
                  diastolicName='blood_pressure_diastolic'
                  label='Blood Pressure'
                  required={false}
                />
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='bpRecordedDate'>BP Recorded Date</Label>
                <DatePicker
                  value={
                    form.watch('blood_pressure_recorded_at')
                      ? new Date(form.watch('blood_pressure_recorded_at'))
                      : undefined
                  }
                  onValueChange={date => {
                    const dateString = date ? format(date, 'yyyy-MM-dd') : '';
                    form.setValue('blood_pressure_recorded_at', dateString, {
                      shouldValidate: true,
                    });
                  }}
                  placeholder='Select BP recorded date'
                  maxDate={new Date()}
                />
              </div>
            </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Medical History */}
          <Collapsible open={isMedicalOpen} onOpenChange={setIsMedicalOpen} className='rounded-lg border border-slate-200 bg-white px-4'>
            <CollapsibleTrigger asChild>
              <button type='button' className='flex w-full items-center justify-between py-3 text-sm font-semibold text-[#60378B] hover:text-[#4c2c6f]'>
                <span className='flex items-center gap-2'>
                  <HeartPulse className='h-4 w-4' />
                  MEDICAL HISTORY
                  {(form.formState.errors.medication_allergies || form.formState.errors.no_known_allergies || form.formState.errors.medical_history) && (
                    <span className='h-2 w-2 rounded-full bg-red-500' />
                  )}
                </span>
                <div className='flex items-center gap-2'>
                  <span className='text-xs font-normal text-slate-400'>Required</span>
                  <ChevronDown className={cn('h-4 w-4 transition-transform', isMedicalOpen && 'rotate-180')} />
                </div>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className='space-y-4 pb-4 pt-2'>
            <div className='space-y-4'>
              <div className='flex items-center space-x-2'>
                <Checkbox
                  id='no-known-allergies'
                  checked={form.watch('no_known_allergies') || false}
                  onCheckedChange={checked => {
                    const isChecked = checked as boolean;
                    form.setValue('no_known_allergies', isChecked, { shouldValidate: true });
                    if (isChecked) {
                      form.setValue('medication_allergies', [], { shouldValidate: true });
                    }
                  }}
                />
                <Label htmlFor='no-known-allergies' className='cursor-pointer font-normal'>
                  No Known Allergies <span className='text-red-500'>*</span>
                </Label>
              </div>
              {form.formState.errors.no_known_allergies && (
                <p className='text-[0.8rem] text-red-500'>
                  {form.formState.errors.no_known_allergies.message}
                </p>
              )}

              {!form.watch('no_known_allergies') && (
                <div className='space-y-1.5'>
                  <AllergyMultiSelect
                    form={form}
                    name='medication_allergies'
                    label='Medication Allergies'
                    placeholder='Search and select allergies...'
                    required={true}
                    disabled={false}
                  />
                  <p className='text-xs text-slate-500'>
                    Search and select allergies from the NLM Clinical Tables database. Select "No
                    Known Allergies" if you have no allergies.
                  </p>
                </div>
              )}
            </div>

            <MedicalHistoryMultiSelect
              form={form}
              name='medical_history'
              label='Medical History'
              placeholder='Select medical conditions...'
            />
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Submit Button */}
          <div className='flex justify-end border-t pt-6'>
            <Button
              type='submit'
              className='h-11 rounded-lg bg-brand-teal px-8 hover:bg-brand-teal/90'
              disabled={isLoading}
            >
              {isLoading ? (
                <div className='flex items-center space-x-2'>
                  <div className='h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent' />
                  <span>Submitting...</span>
                </div>
              ) : (
                <span>{patientData ? 'Update Information' : 'Submit Information'}</span>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default PatientSelfServiceForm;
